/**
 * Existing datasets as a searchable list of radios. Datasets with no items
 * cannot be picked, so they are tucked behind "Show n without items".
 * Archived ones are not offered; a search that finds only those says so.
 */

import { FileSpreadsheet, Radio, Search } from "lucide-react";
import { useState } from "react";
import { Pill } from "@/components/ui/pill";
import { ArchivedMatches } from "@/features/archive/archived";
import type { Dataset } from "@/features/datasets/types";
import { formatCount, formatDate } from "@/lib/format";

export function DatasetList(props: {
  datasets: Dataset[];
  datasetId: string | null;
  onChange: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [showEmpty, setShowEmpty] = useState(false);
  const q = query.trim().toLowerCase();
  const matching = props.datasets.filter((d) => d.name.toLowerCase().includes(q));
  const empty = matching.filter(isEmpty);
  const shown = showEmpty ? matching : matching.filter((d) => !isEmpty(d));
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-[9px] border border-line">
      <label className="relative border-b border-line-soft">
        <span className="sr-only">Search datasets</span>
        <Search aria-hidden className="absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-faint" />
        <input
          className="h-10 w-full bg-transparent pr-3 pl-8 text-[13px] outline-none placeholder:text-faint"
          value={query}
          placeholder={`Search ${props.datasets.length} datasets`}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <div
        role="radiogroup"
        aria-label="Dataset"
        className="flex max-h-[420px] flex-col overflow-y-auto p-1.5"
      >
        {shown.map((d) => (
          <DatasetRow
            key={d.id}
            dataset={d}
            checked={d.id === props.datasetId}
            onPick={() => props.onChange(d.id)}
          />
        ))}
        {q && !matching.length && (
          <p className="flex flex-col gap-1 px-3 py-6 text-center text-xs text-faint">
            No dataset matches.
            <ArchivedMatches kind="dataset" q={query} />
          </p>
        )}
      </div>
      {empty.length > 0 && (
        <button
          type="button"
          onClick={() => setShowEmpty(!showEmpty)}
          className="border-t border-line-soft px-3 py-2 text-left text-xs text-muted hover:text-ink"
        >
          {showEmpty ? "Hide" : "Show"} {formatCount(empty.length)} without items
        </button>
      )}
    </div>
  );
}

const isEmpty = (d: Dataset) => d.kind !== "live" && !d.latest_version?.item_count;

function DatasetRow({
  dataset: d,
  checked,
  onPick,
}: {
  dataset: Dataset;
  checked: boolean;
  onPick: () => void;
}) {
  const live = d.kind === "live";
  const Icon = live ? Radio : FileSpreadsheet;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={isEmpty(d)}
      onClick={onPick}
      className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-run-bg/60" : "hover:bg-hover"
      }`}
    >
      <span
        aria-hidden
        className={`flex size-7 shrink-0 items-center justify-center rounded-md ${
          live ? "bg-gold-bg text-gold" : "bg-chip text-chip-ink"
        }`}
      >
        <Icon className="size-3.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={`truncate text-[13px] ${checked ? "font-semibold text-ink" : "font-medium text-body"}`}
        >
          {d.name}
        </span>
        <span className="truncate text-[11px] text-muted">
          {live
            ? d.latest_version
              ? `Live · latest v${d.latest_version.version}`
              : "Live · pulls traces each run"
            : d.latest_version
              ? `v${d.latest_version.version} · ${formatCount(d.latest_version.item_count)} items`
              : "No items yet"}
          {" · "}
          {formatDate(d.latest_version?.created_at ?? d.created_at)}
        </span>
      </span>
      {d.is_golden && <Pill tone="run">Golden</Pill>}
    </button>
  );
}
